import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  Tooltip,
  LinearProgress,
  Checkbox,
  FormControlLabel,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  CompareArrows as CompareArrowsIcon,
  ArrowForward as ArrowForwardIcon,
  InfoOutlined as InfoOutlinedIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import Navbar from '../../../components/Navbar';
import { createAllPhaseMenuGroups } from '../../../utils/createAllPhaseMenuGroups';
import axiosInstance from '../../../utils/axiosInstance';
import config from '../../../config.json';

export default function PostClassDiagnosisCenter() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [recentCourses, setRecentCourses] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [courseStats, setCourseStats] = useState({});
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [compareData, setCompareData] = useState([]);
  const [showComparison, setShowComparison] = useState(false);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(config.getCourseDispatches || 'api/course-dispatches');
      const courses = response.data?.success ? (response.data.data || []) : [];
      
      const sortedCourses = courses
        .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
        .slice(0, 5);
      
      setRecentCourses(sortedCourses);
      setAllCourses(courses);
      await fetchCourseDiagnosisData(courses);
    } catch (error) {
      console.error('獲取課程列表失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseDiagnosisData = async (courses) => {
    const stats = {};
    const coursesToAnalyze = courses.slice(0, 5);
    
    const promises = coursesToAnalyze.map(async (course) => {
      try {
        const detailResponse = await axiosInstance.get(`${config.getCourseDispatchDetail}${course.id}/detail`);
        const detail = detailResponse.data?.data || {};
        const matrixStatus = detail.taskGroupMatrix?.matrixStatus || {};
        const tasks = detail.taskGroupMatrix?.tasks || [];
        const groups = detail.taskGroupMatrix?.groups || [];
        
        const [ideaNodesResp, questionNodesResp] = await Promise.allSettled([
          axiosInstance.get(`${config.getWallNodes}${course.id}/idea`),
          axiosInstance.get(`${config.getWallNodes}${course.id}/question`)
        ]);
        
        const ideaNodes = ideaNodesResp.status === 'fulfilled' && ideaNodesResp.value.data?.success 
          ? (ideaNodesResp.value.data.data || []) 
          : [];
        const questionNodes = questionNodesResp.status === 'fulfilled' && questionNodesResp.value.data?.success
          ? (questionNodesResp.value.data.data || [])
          : [];
        const allNodes = [...ideaNodes, ...questionNodes];
        
        const totalTasks = tasks.length;
        const totalGroups = groups.length;
        
        let completedTasks = 0;
        let totalTaskGroups = 0;
        tasks.forEach(task => {
          groups.forEach(group => {
            const key = `${task.id}_${group.id}`;
            const status = matrixStatus[key]?.status;
            totalTaskGroups++;
            if (status === 'completed') {
              completedTasks++;
            }
          });
        });
        const taskCompletionRate = totalTaskGroups > 0 ? Math.round((completedTasks / totalTaskGroups) * 100) : 0;
        
        const activeStudents = new Set(allNodes.map(n => n.author_id || n.Author?.id).filter(Boolean)).size;
        const totalStudents = groups.reduce((sum, g) => sum + (g.member_count || g.members?.length || 0), 0);
        const participationRate = totalStudents > 0 ? Math.round((activeStudents / totalStudents) * 100) : 0;
        
        return {
          courseId: course.id,
          stats: {
            totalTasks,
            totalGroups,
            totalStudents,
            activeStudents,
            participationRate,
            taskCompletionRate,
            completedTasks,
            totalTaskGroups
          }
        };
      } catch (error) {
        console.error(`獲取課程 ${course.id} 診斷數據失敗:`, error);
        return {
          courseId: course.id,
          stats: {
            totalTasks: 0,
            totalGroups: 0,
            totalStudents: 0,
            activeStudents: 0,
            participationRate: 0,
            taskCompletionRate: 0,
            completedTasks: 0,
            totalTaskGroups: 0
          }
        };
      }
    });
    
    const results = await Promise.all(promises);
    results.forEach(({ courseId, stats: courseStats }) => {
      stats[courseId] = courseStats;
    });
    
    setCourseStats(stats);
  };

  const handleViewDiagnosis = (courseDispatchId) => {
    navigate(`/teacher/implementation/community-applications/${courseDispatchId}/analytics?tab=1`);
  };

  const handleCourseToggle = (courseId) => {
    setSelectedCourses(prev => {
      if (prev.includes(courseId)) {
        return prev.filter(id => id !== courseId);
      } else if (prev.length < 5) {
        return [...prev, courseId];
      } else {
        return prev;
      }
    });
    setShowComparison(false);
  };

  const handleSelectAll = () => {
    const maxSelect = Math.min(5, allCourses.length);
    const allCourseIds = allCourses.slice(0, maxSelect).map(c => c.id);
    setSelectedCourses(allCourseIds);
    setShowComparison(false);
  };

  const handleDeselectAll = () => {
    setSelectedCourses([]);
    setShowComparison(false);
  };

  const fetchCompareData = async () => {
    try {
      setLoading(true);
      const promises = selectedCourses.map(async (courseId) => {
        try {
          const course = allCourses.find(c => c.id === courseId);
          if (!course) return null;

          const detailResponse = await axiosInstance.get(`${config.getCourseDispatchDetail}${courseId}/detail`);
          const detail = detailResponse.data?.data || {};
          const matrixStatus = detail.taskGroupMatrix?.matrixStatus || {};
          const tasks = detail.taskGroupMatrix?.tasks || [];
          const groups = detail.taskGroupMatrix?.groups || [];
          
          const [ideaNodesResp, questionNodesResp] = await Promise.allSettled([
            axiosInstance.get(`${config.getWallNodes}${courseId}/idea`),
            axiosInstance.get(`${config.getWallNodes}${courseId}/question`)
          ]);
          
          const ideaNodes = ideaNodesResp.status === 'fulfilled' && ideaNodesResp.value.data?.success 
            ? (ideaNodesResp.value.data.data || []) 
            : [];
          const questionNodes = questionNodesResp.status === 'fulfilled' && questionNodesResp.value.data?.success
            ? (questionNodesResp.value.data.data || [])
            : [];
          const allNodes = [...ideaNodes, ...questionNodes];
          
          let completedTasks = 0;
          let totalTaskGroups = 0;
          tasks.forEach(task => {
            groups.forEach(group => {
              const key = `${task.id}_${group.id}`;
              const status = matrixStatus[key]?.status;
              totalTaskGroups++;
              if (status === 'completed') {
                completedTasks++;
              }
            });
          });
          
          const activeStudents = new Set(allNodes.map(n => n.author_id || n.Author?.id).filter(Boolean)).size;
          const totalStudents = groups.reduce((sum, g) => sum + (g.member_count || g.members?.length || 0), 0);
          
          return {
            courseId,
            courseName: course.title || course.Class?.name || `課程 ${courseId}`,
            className: course.Class?.name || '未命名班級',
            totalTasks: tasks.length,
            totalGroups: groups.length,
            totalStudents,
            activeStudents,
            participationRate: totalStudents > 0 ? Math.round((activeStudents / totalStudents) * 100) : 0,
            taskCompletionRate: totalTaskGroups > 0 ? Math.round((completedTasks / totalTaskGroups) * 100) : 0,
            totalNodes: allNodes.length,
            ideaCount: ideaNodes.length,
            questionCount: questionNodes.length
          };
        } catch (error) {
          console.error(`獲取課程 ${courseId} 數據失敗:`, error);
          return null;
        }
      });

      const results = await Promise.all(promises);
      setCompareData(results.filter(r => r !== null));
    } catch (error) {
      console.error('獲取比較數據失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = () => {
    if (selectedCourses.length < 2) {
      alert('請至少選擇2個課程進行比較');
      return;
    }
    setShowComparison(true);
    fetchCompareData();
  };

  // A-3: 使用分層架構選單
  const allPhaseMenuGroups = createAllPhaseMenuGroups('diagnosis', 'diagnosis', navigate);

  const overallStats = {
    totalCourses: allCourses.length,
    totalStudents: Object.values(courseStats).reduce((sum, s) => sum + (s.totalStudents || 0), 0),
    activeStudents: Object.values(courseStats).reduce((sum, s) => sum + (s.activeStudents || 0), 0),
    avgParticipationRate: allCourses.length > 0 
      ? Math.round(Object.values(courseStats).reduce((sum, s) => sum + (s.participationRate || 0), 0) / allCourses.length)
      : 0,
    avgTaskCompletionRate: allCourses.length > 0
      ? Math.round(Object.values(courseStats).reduce((sum, s) => sum + (s.taskCompletionRate || 0), 0) / allCourses.length)
      : 0
  };

  if (loading) {
    return (
      <div className="teacher-layout">
        <div className="main-content-v2">
          <Navbar 
            allPhaseMenuGroups={allPhaseMenuGroups}
            currentPhase="diagnosis"
            onPhaseChange={(phase) => {
              if (phase === 'preparation') {
                navigate('/teacher/preparation');
              } else if (phase === 'implementation') {
                navigate('/teacher/implementation');
              } else if (phase === 'diagnosis') {
                navigate('/teacher/diagnosis');
              }
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        </div>
      </div>
    );
  }

  return (
    <div className="teacher-layout">
      <div className="main-content-v2">
        <Navbar 
          allPhaseMenuGroups={allPhaseMenuGroups}
          currentPhase="diagnosis"
          onPhaseChange={(phase) => {
            if (phase === 'preparation') {
              navigate('/teacher/preparation');
            } else if (phase === 'implementation') {
              navigate('/teacher/implementation');
            } else if (phase === 'diagnosis') {
              navigate('/teacher/diagnosis');
            }
          }}
        />

        <div className="class-header-row">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', width: '100%' }}>
            <h1 className="main-title">課後診斷中心</h1>
            <Tooltip title="課後診斷提供課程參與度、任務成效、學習行為等深度分析">
              <IconButton 
                size="small"
                sx={{
                  position: 'absolute',
                  right: 0,
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    color: 'text.primary'
                  }
                }}
              >
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </div>

        {/* 總體統計 */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  總課程數
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {overallStats.totalCourses}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  總學生數
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {overallStats.totalStudents}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  平均參與率
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {overallStats.avgParticipationRate}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  平均完成率
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  {overallStats.avgTaskCompletionRate}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* 跨課程比較 */}
          {!showComparison ? (
            <Grid item xs={12}>
              <Card 
                sx={{ 
                  background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)',
                  border: '1px solid #e0e6ed',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  {/* 標題區域 */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    mb: 3,
                    pb: 2,
                    borderBottom: '2px solid #e8f4f8'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ 
                        p: 1.5, 
                        borderRadius: '12px', 
                        background: 'linear-gradient(135deg, #2b687f 0%, #1e4d5f 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <CompareArrowsIcon sx={{ color: '#fff', fontSize: 28 }} />
                      </Box>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e4d5f', mb: 0.5 }}>
                          跨課程比較
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          選擇多個課程進行對比分析（最多5個，至少2個）
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <Button
                        variant="outlined"
                        onClick={handleSelectAll}
                        disabled={selectedCourses.length >= 5 || allCourses.length === 0}
                        sx={{
                          textTransform: 'none',
                          borderRadius: '8px',
                          px: 2.5,
                          py: 1,
                          borderColor: '#2b687f',
                          color: '#2b687f',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          '&:hover': {
                            borderColor: '#1e4d5f',
                            backgroundColor: '#e8f4f8',
                            color: '#1e4d5f'
                          },
                          '&.Mui-disabled': {
                            borderColor: '#e0e0e0',
                            color: '#bdbdbd'
                          }
                        }}
                      >
                        全選
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleDeselectAll}
                        disabled={selectedCourses.length === 0}
                        sx={{
                          textTransform: 'none',
                          borderRadius: '8px',
                          px: 2.5,
                          py: 1,
                          borderColor: '#9e9e9e',
                          color: '#616161',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          '&:hover': {
                            borderColor: '#757575',
                            backgroundColor: '#f5f5f5',
                            color: '#424242'
                          },
                          '&.Mui-disabled': {
                            borderColor: '#e0e0e0',
                            color: '#bdbdbd'
                          }
                        }}
                      >
                        取消全選
                      </Button>
                    </Box>
                  </Box>
                  
                  {/* 選擇提示 */}
                  {selectedCourses.length >= 5 && (
                    <Alert 
                      severity="info" 
                      sx={{ 
                        mb: 3,
                        borderRadius: '8px',
                        backgroundColor: '#e3f2fd',
                        '& .MuiAlert-icon': {
                          color: '#1976d2'
                        }
                      }}
                    >
                      已選擇 {selectedCourses.length} 個課程，達到上限
                    </Alert>
                  )}

                  {/* 課程選擇區域 */}
                  {allCourses.length > 0 ? (
                    <Grid container spacing={2.5} sx={{ mb: 3 }}>
                      {allCourses.map((course) => {
                        const isSelected = selectedCourses.includes(course.id);
                        return (
                          <Grid item xs={12} sm={6} md={4} lg={2.4} key={course.id}>
                            <Card
                              sx={{
                                border: isSelected ? '2px solid #2b687f' : '1.5px solid #e0e6ed',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                background: isSelected 
                                  ? 'linear-gradient(135deg, #e8f4f8 0%, #ffffff 100%)' 
                                  : '#ffffff',
                                boxShadow: isSelected 
                                  ? '0 8px 16px rgba(43, 104, 127, 0.15)' 
                                  : '0 2px 4px rgba(0, 0, 0, 0.05)',
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  boxShadow: isSelected 
                                    ? '0 12px 24px rgba(43, 104, 127, 0.2)' 
                                    : '0 4px 12px rgba(0, 0, 0, 0.1)',
                                  borderColor: isSelected ? '#2b687f' : '#b0bec5'
                                }
                              }}
                              onClick={() => handleCourseToggle(course.id)}
                            >
                              <CardContent sx={{ p: 2.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() => handleCourseToggle(course.id)}
                                    sx={{
                                      color: '#2b687f',
                                      '&.Mui-checked': {
                                        color: '#2b687f'
                                      },
                                      p: 0.5
                                    }}
                                  />
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography 
                                      variant="subtitle1" 
                                      sx={{ 
                                        fontWeight: 600, 
                                        color: '#1e4d5f',
                                        mb: 0.5,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        lineHeight: 1.4
                                      }}
                                    >
                                      {course.title || course.Class?.name || `課程 ${course.id}`}
                                    </Typography>
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary"
                                      sx={{
                                        display: 'block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      {course.Class?.name || '未命名班級'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    <Box sx={{ 
                      textAlign: 'center', 
                      py: 6,
                      borderRadius: '12px',
                      backgroundColor: '#fafafa',
                      border: '2px dashed #e0e0e0'
                    }}>
                      <Typography variant="body1" color="text.secondary">
                        暫無課程資料
                      </Typography>
                    </Box>
                  )}

                  {/* 操作按鈕區域 */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    gap: 2,
                    pt: 2,
                    borderTop: '2px solid #e8f4f8'
                  }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setSelectedCourses([]);
                        setShowComparison(false);
                      }}
                      disabled={selectedCourses.length === 0}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '10px',
                        px: 4,
                        py: 1.25,
                        borderColor: '#9e9e9e',
                        color: '#616161',
                        fontWeight: 600,
                        fontSize: '0.9375rem',
                        '&:hover': {
                          borderColor: '#757575',
                          backgroundColor: '#f5f5f5',
                          color: '#424242'
                        },
                        '&.Mui-disabled': {
                          borderColor: '#e0e0e0',
                          color: '#bdbdbd'
                        }
                      }}
                    >
                      清除選擇
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<CompareArrowsIcon />}
                      onClick={handleCompare}
                      disabled={selectedCourses.length < 2}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '10px',
                        px: 4,
                        py: 1.25,
                        background: selectedCourses.length >= 2
                          ? 'linear-gradient(135deg, #2b687f 0%, #1e4d5f 100%)'
                          : 'rgba(0, 0, 0, 0.12)',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.9375rem',
                        boxShadow: selectedCourses.length >= 2
                          ? '0 4px 12px rgba(43, 104, 127, 0.35)'
                          : 'none',
                        '&:hover': {
                          background: selectedCourses.length >= 2
                            ? 'linear-gradient(135deg, #1e4d5f 0%, #153d4a 100%)'
                            : 'rgba(0, 0, 0, 0.12)',
                          boxShadow: selectedCourses.length >= 2
                            ? '0 6px 16px rgba(43, 104, 127, 0.45)'
                            : 'none',
                          transform: selectedCourses.length >= 2 ? 'translateY(-2px)' : 'none'
                        },
                        '&.Mui-disabled': {
                          background: 'rgba(0, 0, 0, 0.12)',
                          color: 'rgba(0, 0, 0, 0.26)',
                          boxShadow: 'none'
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      開始比較 {selectedCourses.length > 0 && `(${selectedCourses.length})`}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Card 
                sx={{ 
                  background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)',
                  border: '1px solid #e0e6ed',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    mb: 3,
                    pb: 2,
                    borderBottom: '2px solid #e8f4f8'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ 
                        p: 1.5, 
                        borderRadius: '12px', 
                        background: 'linear-gradient(135deg, #2b687f 0%, #1e4d5f 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <CompareArrowsIcon sx={{ color: '#fff', fontSize: 28 }} />
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e4d5f' }}>
                        比較結果
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setShowComparison(false);
                        setSelectedCourses([]);
                      }}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '6px',
                        px: 2,
                        py: 0.5,
                        borderColor: '#9e9e9e',
                        color: '#616161',
                        fontWeight: 500,
                        fontSize: '0.8125rem',
                        minWidth: 'auto',
                        '&:hover': {
                          borderColor: '#757575',
                          backgroundColor: '#f5f5f5',
                          color: '#424242'
                        }
                      }}
                    >
                      重新選擇
                    </Button>
                  </Box>

                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                      <CircularProgress size={48} sx={{ color: '#2b687f' }} />
                    </Box>
                  ) : compareData.length > 0 ? (
                    <TableContainer 
                      component={Paper} 
                      sx={{ 
                        borderRadius: '12px',
                        border: '1px solid #e0e6ed',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                        overflow: 'hidden'
                      }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                            <TableCell sx={{ fontWeight: 700, color: '#1e4d5f', fontSize: '0.9375rem' }}>
                              課程名稱
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#1e4d5f', fontSize: '0.9375rem' }}>
                              學生數
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#1e4d5f', fontSize: '0.9375rem' }}>
                              參與率
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#1e4d5f', fontSize: '0.9375rem' }}>
                              任務完成率
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#1e4d5f', fontSize: '0.9375rem' }}>
                              總節點數
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#1e4d5f', fontSize: '0.9375rem' }}>
                              想法節點
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#1e4d5f', fontSize: '0.9375rem' }}>
                              問題節點
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {compareData.map((data, index) => (
                            <TableRow 
                              key={data.courseId}
                              sx={{
                                '&:hover': {
                                  backgroundColor: '#f8f9fa'
                                },
                                '&:nth-of-type(even)': {
                                  backgroundColor: '#fafbfc'
                                }
                              }}
                            >
                              <TableCell>
                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e4d5f', mb: 0.5 }}>
                                    {data.courseName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {data.className}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  icon={<PeopleIcon sx={{ fontSize: 14 }} />}
                                  label={`${data.activeStudents}/${data.totalStudents}`}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#e3f2fd',
                                    color: '#1976d2',
                                    fontWeight: 600,
                                    border: '1px solid #90caf9'
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Box sx={{ minWidth: 120, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2b687f' }}>
                                    {data.participationRate}%
                                  </Typography>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={data.participationRate} 
                                    sx={{ 
                                      height: 8, 
                                      borderRadius: 4,
                                      backgroundColor: '#e0e6ed',
                                      '& .MuiLinearProgress-bar': {
                                        borderRadius: 4,
                                        background: 'linear-gradient(90deg, #2b687f 0%, #1e4d5f 100%)'
                                      }
                                    }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Box sx={{ minWidth: 120, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                                    {data.taskCompletionRate}%
                                  </Typography>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={data.taskCompletionRate} 
                                    sx={{ 
                                      height: 8, 
                                      borderRadius: 4,
                                      backgroundColor: '#e8f5e9',
                                      '& .MuiLinearProgress-bar': {
                                        borderRadius: 4,
                                        background: 'linear-gradient(90deg, #4caf50 0%, #2e7d32 100%)'
                                      }
                                    }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#424242' }}>
                                  {data.totalNodes}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#424242' }}>
                                  {data.ideaCount}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#424242' }}>
                                  {data.questionCount}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert 
                      severity="warning"
                      sx={{
                        borderRadius: '12px',
                        backgroundColor: '#fff3e0',
                        border: '1px solid #ffb74d',
                        '& .MuiAlert-icon': {
                          color: '#f57c00'
                        }
                      }}
                    >
                      無法獲取比較數據
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* 快速診斷 */}
          {!showComparison && (
            <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AssessmentIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">快速診斷</Typography>
                </Box>
                {recentCourses.length > 0 ? (
                  <Grid container spacing={2}>
                    {recentCourses.map((course) => {
                      const stats = courseStats[course.id] || {};
                      return (
                        <Grid item xs={12} sm={6} md={4} key={course.id}>
                          <Card
                            variant="outlined"
                            sx={{
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              '&:hover': {
                                boxShadow: 2,
                                borderColor: 'primary.main'
                              }
                            }}
                            onClick={() => handleViewDiagnosis(course.id)}
                          >
                            <CardContent>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1.5 }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    {course.title || course.Class?.name || `課程 ${course.id}`}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {course.Class?.name || '未命名班級'}
                                  </Typography>
                                </Box>
                                <ArrowForwardIcon sx={{ color: 'text.secondary', ml: 1 }} />
                              </Box>

                              <Box sx={{ mb: 1.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    參與率
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                    {stats.participationRate || 0}%
                                  </Typography>
                                </Box>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={stats.participationRate || 0} 
                                  sx={{ height: 6, borderRadius: 3 }} 
                                />
                              </Box>

                              <Box sx={{ mb: 1.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    任務完成率
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                    {stats.taskCompletionRate || 0}%
                                  </Typography>
                                </Box>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={stats.taskCompletionRate || 0} 
                                  sx={{ height: 6, borderRadius: 3 }}
                                  color="success"
                                />
                              </Box>

                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                  icon={<PeopleIcon sx={{ fontSize: 14 }} />}
                                  label={`${stats.activeStudents || 0}/${stats.totalStudents || 0}`}
                                  size="small"
                                  variant="outlined"
                                />
                                <Chip
                                  icon={<AssignmentIcon sx={{ fontSize: 14 }} />}
                                  label={`${stats.completedTasks || 0}/${stats.totalTaskGroups || 0}`}
                                  size="small"
                                  variant="outlined"
                                />
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                    暫無課程資料
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          )}
        </Grid>
      </div>
    </div>
  );
}
